import { Injectable } from '@angular/core';
import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
} from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, catchError, throwError } from 'rxjs';

/**
 * Why: Global HTTP interceptor to
 * 1) ensure `Content-Type: application/json` is present for JSON requests, and
 * 2) catch all HTTP errors and display a readable message via MatSnackBar.
 */
@Injectable()
export class JsonErrorInterceptor implements HttpInterceptor {
    constructor(private readonly snackBar: MatSnackBar) {}

    intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        let modified = req;

        // Add Accept header if missing to request JSON responses
        if (!modified.headers.has('Accept')) {
            modified = modified.clone({
                headers: modified.headers.set('Accept', 'application/json'),
            });
        }

        // Add Content-Type for requests with a body, except when it's FormData or already set
        const hasBody = modified.body !== null && modified.body !== undefined;
        const isFormData = typeof FormData !== 'undefined' && hasBody && modified.body instanceof FormData;
        const methodHasBody = /^(POST|PUT|PATCH|DELETE)$/i.test(modified.method);

        if (methodHasBody && hasBody && !isFormData && !modified.headers.has('Content-Type')) {
            modified = modified.clone({
                headers: modified.headers.set('Content-Type', 'application/json'),
            });
        }

        return next.handle(modified).pipe(
            catchError((err: unknown) => {
                const msg = this.extractErrorMessage(err);
                // Show a short snackbar with the error message
                try {
                    this.snackBar.open(msg, 'Close', { duration: 5000, horizontalPosition: 'right', verticalPosition: 'top' });
                } catch {
                    // ignore snackbar rendering errors
                }
                return throwError(() => err);
            }),
        );
    }

    private extractErrorMessage(err: unknown): string {
        if (err instanceof HttpErrorResponse) {
            const status = err.status;
            const statusText = err.statusText || 'Error';
            const backendMessage = this.readBackendMessage(err.error);
            return backendMessage
                ? `${status ? `[${status}] ` : ''}${backendMessage}`
                : `${status ? `[${status}] ` : ''}${statusText}`;
        }
        if (err && typeof err === 'object' && 'message' in (err as any)) {
            return String((err as any).message);
        }
        return 'Unexpected error occurred';
    }

    private readBackendMessage(errorPayload: unknown): string | null {
        if (!errorPayload) return null;
        if (typeof errorPayload === 'string') return errorPayload;
        if (typeof errorPayload === 'object') {
            const anyErr = errorPayload as any;
            // Common backend shapes: { message }, { error: { message } }, { detail }
            if (anyErr.message) return String(anyErr.message);
            if (anyErr.error && anyErr.error.message) return String(anyErr.error.message);
            if (anyErr.detail) return String(anyErr.detail);
        }
        return null;
    }
}
